import { useRef, useEffect } from 'react';

/**
 * Configuration object for scanlines animation
 */
interface ScanlinesConfig {
  /** Maximum number of scanlines that can exist simultaneously */
  maxScanlines: number;
  /** Probability of spawning a new scanline per frame (0.0 - 1.0) */
  spawnChance: number;
  /** Minimum width of scanlines in pixels */
  minWidth: number;
  /** Maximum width of scanlines in pixels */
  maxWidth: number;
  /** Minimum speed of scanline movement in pixels per frame */
  minSpeed: number;
  /** Maximum speed of scanline movement in pixels per frame */
  maxSpeed: number;
  /** Opacity of scanlines (0.0 - 1.0) */
  opacity: number;
  /** Probability that a scanline will disappear before reaching the edge (0.0 - 1.0) */
  earlyDisappearChance: number;
  /** Minimum lifespan in frames before a scanline can disappear early */
  minLifespan: number;
}

/**
 * Individual scanline data structure
 */
interface Scanline {
  /** Current x position */
  x: number;
  /** Movement speed (positive = right, negative = left) */
  speed: number;
  /** Width of the scanline */
  width: number;
  /** Current age in frames */
  age: number;
  /** Whether this scanline is marked for removal */
  isDead: boolean;
}

/**
 * Scanlines component that renders animated vertical scan lines over the background
 * @returns JSX element containing the scanlines canvas
 */
const Scanlines = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /** Configuration object for scanlines behavior */
  const config: ScanlinesConfig = {
    maxScanlines: 20,
    spawnChance: 0.02,
    minWidth: 2,
    maxWidth: 7,
    minSpeed: 0.5,
    maxSpeed: 5.0,
    opacity: 0.15,
    earlyDisappearChance: 0.008,
    minLifespan: 30,
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationFrameId: number;
    let scanlines: Scanline[] = [];

    /**
     * Handle canvas resizing to match window dimensions
     */
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    /**
     * Generate a random number between min and max (inclusive)
     * @param min - Minimum value
     * @param max - Maximum value
     * @returns Random number in range
     */
    const randomBetween = (min: number, max: number): number => {
      return Math.random() * (max - min) + min;
    };

    /**
     * Create a new scanline with random properties
     * @param canvasWidth - Width of the canvas
     * @returns New scanline object
     */
    const createScanline = (canvasWidth: number): Scanline => {
      const goingRight = Math.random() < 0.5;
      return {
        x: goingRight ? -config.maxWidth : canvasWidth + config.maxWidth,
        speed: goingRight 
          ? randomBetween(config.minSpeed, config.maxSpeed)
          : -randomBetween(config.minSpeed, config.maxSpeed),
        width: randomBetween(config.minWidth, config.maxWidth),
        age: 0,
        isDead: false,
      };
    };

    /**
     * Update scanline positions and handle lifecycle
     * @param canvasWidth - Width of the canvas
     */
    const updateScanlines = (canvasWidth: number) => {
      // Update existing scanlines
      scanlines.forEach(scanline => {
        scanline.x += scanline.speed;
        scanline.age++;

        // Check if scanline should disappear early
        if (scanline.age > config.minLifespan && Math.random() < config.earlyDisappearChance) {
          scanline.isDead = true;
        }

        // Check if scanline has moved off screen
        if (scanline.speed > 0 && scanline.x > canvasWidth + scanline.width) {
          scanline.isDead = true;
        } else if (scanline.speed < 0 && scanline.x < -scanline.width) {
          scanline.isDead = true;
        }
      });

      // Remove dead scanlines
      scanlines = scanlines.filter(scanline => !scanline.isDead);

      // Spawn new scanlines
      if (scanlines.length < config.maxScanlines && Math.random() < config.spawnChance) {
        scanlines.push(createScanline(canvasWidth));
      }
    };

    /**
     * Render all active scanlines to the canvas
     * @param canvasWidth - Width of the canvas
     * @param canvasHeight - Height of the canvas
     */
    const renderScanlines = (canvasWidth: number, canvasHeight: number) => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      
      ctx.fillStyle = `rgba(0, 0, 0, ${config.opacity})`;
      
      scanlines.forEach(scanline => {
        ctx.fillRect(scanline.x, 0, scanline.width, canvasHeight);
      });
    };

    /**
     * Main animation loop
     */
    const animate = () => {
      const { width, height } = canvas;
      
      updateScanlines(width);
      renderScanlines(width, height);
      
      animationFrameId = requestAnimationFrame(animate);
    };

    // Initialize and start animation
    handleResize();
    animate();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [config]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default Scanlines;
